import { Select as SelectPrimitive } from "@base-ui/react/select";
import { IconCheck, IconChevronDown, IconWorld } from "@tabler/icons-react";
import type { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatReportingClock,
  isReportingTimeZone,
  localTimeZone,
  type REPORTING_TIME_ZONES,
  type ReportingTimeZone,
  timeZoneCity,
} from "@/lib/reporting-time-zone";
import { cn } from "@/lib/utils";
import { FilterTriggerIcon, reportingFilterTriggerClassName } from "./reporting-period-select";

interface ReportingTimeZoneSelectProps {
  value: ReportingTimeZone;
  onValueChange: (value: ReportingTimeZone) => void;
}

/**
 * Reporting time zone select (Paper "Time zone · short list with clocks"): zone name +
 * live clock on the trigger; the viewer's local zone, the short list and UTC, each row
 * with its current time.
 */
export function ReportingTimeZoneSelect({
  value,
  onValueChange,
}: ReportingTimeZoneSelectProps): React.ReactElement {
  return <ShortListTimeZoneSelect value={value} onValueChange={onValueChange} />;
}

export function ReportingClock({ value }: { value: ReportingTimeZone }): React.ReactElement {
  const [now, setNow] = useState(() => new Date());
  const clock = formatReportingClock(now, value);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(interval);
  }, []);

  return (
    <time
      dateTime={now.toISOString()}
      title={clock.abbreviation}
      className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground"
    >
      {clock.dateTime}
    </time>
  );
}

const TIME_ZONE_LABEL_KEYS: Record<(typeof REPORTING_TIME_ZONES)[number], string> = {
  "America/Los_Angeles": "reportingTimeZone.pacific",
  "America/New_York": "reportingTimeZone.eastern",
  "Europe/London": "reportingTimeZone.london",
  "Europe/Paris": "reportingTimeZone.centralEurope",
  "Australia/Sydney": "reportingTimeZone.sydney",
  UTC: "reportingTimeZone.utc",
  "Asia/Shanghai": "reportingTimeZone.beijing",
};

/** Named zones read by name; any other zone (the viewer's own) reads by its city. */
export function timeZoneLabel(t: TFunction<"layout">, timeZone: ReportingTimeZone): string {
  const key = TIME_ZONE_LABEL_KEYS[timeZone as keyof typeof TIME_ZONE_LABEL_KEYS];
  return key ? t(key) : timeZoneCity(timeZone);
}

// Short list between the Local row and UTC; Beijing joins only when it is the saved choice.
const SHORT_LIST: readonly ReportingTimeZone[] = [
  "America/Los_Angeles",
  "America/New_York",
  "Europe/London",
  "Europe/Paris",
  "Australia/Sydney",
];
const LOCAL_ITEM = "local";

function useMinuteClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(interval);
  }, []);
  return now;
}

function ShortListTimeZoneSelect({
  value,
  onValueChange,
}: ReportingTimeZoneSelectProps): React.ReactElement {
  const { t, i18n } = useTranslation("layout");
  const now = useMinuteClock();
  const language = i18n.resolvedLanguage ?? "en-US";
  const local = localTimeZone();
  const named = [...SHORT_LIST, "UTC"];
  // The Local row stands for the viewer's zone only when no named row already does.
  const localIsNamed = named.includes(local);
  const extra = !named.includes(value) && value !== local ? value : null;
  const selected = !localIsNamed && value === local ? LOCAL_ITEM : value;
  const clockOf = (timeZone: ReportingTimeZone) =>
    new Intl.DateTimeFormat(language, {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).format(now);
  const dateOf = (timeZone: ReportingTimeZone) =>
    new Intl.DateTimeFormat(language, { timeZone, month: "short", day: "numeric" }).format(now);
  const label = timeZoneLabel(t, value);

  function row(item: string, timeZone: ReportingTimeZone, name: React.ReactNode) {
    const otherDay = dateOf(timeZone) !== dateOf(value);
    return (
      <SelectPrimitive.Item
        key={item}
        value={item}
        className="group/item flex h-8 shrink-0 cursor-default items-center gap-2 rounded-xs pr-1.5 pl-2 text-xs text-text-secondary outline-none select-none data-highlighted:bg-control-highlight data-selected:font-medium data-selected:text-ink"
      >
        <SelectPrimitive.ItemText className="flex min-w-0 grow basis-0 items-baseline gap-1.5 truncate">
          {name}
        </SelectPrimitive.ItemText>
        <span className="min-w-[84px] shrink-0 whitespace-nowrap text-right font-normal tabular-nums text-muted-foreground">
          {otherDay ? <span className="text-badge">{dateOf(timeZone)} </span> : null}
          {clockOf(timeZone)}
        </span>
        <span className="flex size-5 shrink-0 items-center justify-center">
          <SelectPrimitive.ItemIndicator className="flex items-center justify-center">
            <IconCheck className="size-[15px] text-ink" stroke={2} aria-hidden />
          </SelectPrimitive.ItemIndicator>
        </span>
      </SelectPrimitive.Item>
    );
  }

  return (
    <SelectPrimitive.Root<string>
      value={selected}
      onValueChange={(next) => {
        const timeZone = next === LOCAL_ITEM ? local : next;
        if (isReportingTimeZone(timeZone)) onValueChange(timeZone);
      }}
    >
      <SelectPrimitive.Trigger
        aria-label={`${t("reportingTimeZone.title")}: ${label}`}
        title={label}
        className={reportingFilterTriggerClassName}
      >
        <FilterTriggerIcon icon={IconWorld} />
        <span className="min-w-0 truncate">{label}</span>
        <time
          dateTime={now.toISOString()}
          className="text-xs font-normal tabular-nums text-muted-foreground"
        >
          {clockOf(value)}
        </time>
        <FilterTriggerIcon icon={IconChevronDown} chevron />
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Positioner
          side="bottom"
          align="end"
          sideOffset={4}
          alignItemWithTrigger={false}
          className="isolate z-50 outline-none"
        >
          <SelectPrimitive.Popup className="popup-motion w-[280px] rounded-md bg-popover p-1 text-popover-foreground shadow-[var(--paper-flyout-shadow)] outline-none">
            <SelectPrimitive.List>
              {localIsNamed
                ? null
                : row(
                    LOCAL_ITEM,
                    local,
                    <>
                      <span className="font-medium text-ink">{t("reportingTimeZone.local")}</span>
                      <span className="truncate font-normal text-muted-foreground">
                        {timeZoneCity(local)}
                      </span>
                    </>,
                  )}
              {localIsNamed ? null : <MenuDivider />}
              {SHORT_LIST.map((timeZone) => row(timeZone, timeZone, timeZoneLabel(t, timeZone)))}
              {extra ? row(extra, extra, timeZoneLabel(t, extra)) : null}
              <MenuDivider />
              {row("UTC", "UTC", timeZoneLabel(t, "UTC"))}
            </SelectPrimitive.List>
          </SelectPrimitive.Popup>
        </SelectPrimitive.Positioner>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

function MenuDivider(): React.ReactElement {
  return (
    <div aria-hidden className={cn("py-1")}>
      <div className="h-px bg-line" />
    </div>
  );
}
